<template>
  <Table :columns="columns" :data="processedClaims" :is-row-clickable="true" @row-click="goToClaim">
    <template #friendlyId="{ row }">
      <div class="flex flex-col">
        <div class="flex items-center gap-1.5">
          <p>{{ row.friendlyId }}</p>
          <ExclamationCircleIcon v-if="hasRequiredAction(row)" class="w-4 h-4 text-yellow-500" />
          <span
            v-if="row.fraudSuspicion"
            data-test="fraud-suspicion-icon"
            :title="t('fraud_suspicion_badge')"
            class="flex"
          >
            <ShieldExclamationIcon class="w-4 h-4 text-red-600" />
          </span>
        </div>
        <div class="mt-2">
          <FieldSpan translation-prefix="claim.status" :class="['ml-3', statusStyle(row.status)]" :value="row.status" />
        </div>
      </div>
    </template>
    <template #ticketStatus="{ row }">
      <div class="flex flex-col">
        <div class="flex">
          <FieldSpan
            translation-prefix="claim.ticket_status"
            :class="['ml-3', ticketStatusStyle(row.ticketStatus)]"
            :value="row.ticketStatus"
          />
        </div>
      </div>
    </template>
    <template #filedDate="{ row }">
      <p>{{ row.filedDate }}</p>
    </template>
    <template #customer="{ row }">
      <div class="text-gray-800 space-y-1">
        <div class="font-semibold text-sm">{{ row.customer.firstName }} {{ row.customer.lastName }}</div>
        <div class="text-xs text-gray-600">
          {{ row.customer.email }}
        </div>
      </div>
    </template>

    <template #productTitle="{ row }">
      <div class="max-w-xs break-words whitespace-normal">
        <p class="leading-relaxed">{{ row.claimProduct?.title }}</p>
        <TableLink :to="`/stores/${row.store.id}`" class="block text-xs mt-1" data-test="store-link">
          {{ row.store.salesChannel }}
        </TableLink>
      </div>
    </template>

    <template #claimType="{ row }">
      <div class="max-w-xs break-words leading-relaxed whitespace-normal">
        {{ $t(`claim.claim_type.${row.claimType}`) }}
      </div>
    </template>

    <template #assignee="{ row }">
      <div data-test="claim-assignee">
        <InitialsAvatar v-if="row.assignee" :name="row.assignee.fullName" />
        <span v-else>—</span>
      </div>
    </template>

    <template #customButton="{ row }">
      <PauseIcon
        v-if="claimTicketStatusOpen(row)"
        class="h-5 cursor-pointer text-orange-400 inline-block"
        @click.stop="() => this.$emit('pause', row)"
      />
    </template>
  </Table>
</template>

<script>
import { useI18n } from "vue-i18n";
import { ExclamationCircleIcon, ShieldExclamationIcon } from "@heroicons/vue/solid";
import { Table, FieldSpan, InitialsAvatar, TableLink } from "@estaly/ui";
import { mapGetters } from "vuex";
import { PauseIcon } from "@heroicons/vue/outline";

export default {
  components: { PauseIcon, FieldSpan, InitialsAvatar, Table, ExclamationCircleIcon, ShieldExclamationIcon, TableLink },
  props: {
    claims: {
      type: Array,
      required: true,
    },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    ...mapGetters("claims", ["hasRequiredAction", "claimTicketStatusOpen", "ticketStatusStyle", "statusStyle"]),

    columns() {
      return [
        { label: "Identifiant", field: "friendlyId", slot: "friendlyId" },
        { label: "Produit", field: "productTitle", slot: "productTitle" },
        { label: "Type", field: "claimType", slot: "claimType" },
        { label: "Etat du dossier", field: "ticketStatus", slot: "ticketStatus" },
        { label: "Déclaration", field: "filedDate", slot: "filedDate" },
        { label: "Client", field: "customerEmail", slot: "customer" },
        { label: this.t("assignee_column"), field: "assignee", slot: "assignee" },
        { label: "Activité", field: "statusUpdatedAt" },
      ];
    },
    processedClaims() {
      return this.claims.map((claim) => ({
        ...claim,
        rowClass: this.hasRequiredAction(claim) ? "bg-yellow-50" : "",
      }));
    },
  },

  methods: {
    goToClaim(claim) {
      this.$router.push(this.claimPath(claim));
    },

    claimPath(claim) {
      return `/claims/${claim.friendlyId}`;
    },
  },
};
</script>

<i18n src="./ClaimList.i18n.json"></i18n>
